import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import {
  addProductToAllStores,
  createProduct,
  deactiveProduct,
  editProduct,
  getProduct,
  getProductNames,
  getProductsByPage,
  reactiveProduct,
  unAvailableProduct,
} from '../../ApiServices/ProductsService';
import { t } from 'i18next';
import { handleResponse } from '../../ApiServices/ApiHandler';

export const initialState = {
  value: { totalCount: -1, data: [{}], categories: [''], subCategories: [''] },
  loading: false,
  error: null,
  selectedValue: null,
  rowsPerPage: 10,
  pageNo: 0,
  filter: {},
  productNames: [{}],
};

const productsSlice = createSlice({
  name: 'products',
  initialState: initialState,
  reducers: {
    clearProductState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
      state.filter = initialState.filter;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProducts.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchProducts.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchProductById.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = action?.payload?.data;
    });
    builder.addCase(fetchProductById.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.error = action?.payload?.data;
    });
    builder.addCase(fetchProductNames.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.productNames = action?.payload?.data;
    });
  },
});

export const productsState = (state: RootState) => state?.products;
export const { clearProductState } = productsSlice.actions;
export default productsSlice.reducer;

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_PRODUCTS') }));
      return fulfillWithValue(await getProductsByPage(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_PRODUCTS_DETAILS') }));
      return fulfillWithValue(await getProduct(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const modifyProduct = createAsyncThunk(
  'products/modifyProduct',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('MODIFYING_PRODUCTS_DETAILS') }));
      let response: any = await editProduct(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('PRODUCT_UPDATED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(showErrorSnackbar({ message: t('PRODUCT_UPDATE_FAILED') }));
        return rejectWithValue(response);
      }
      // return await handleResponse(
      //   response,
      //   fulfillWithValue,
      //   rejectWithValue,
      //   dispatch
      // );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const addProduct = createAsyncThunk(
  'products/addProduct',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('ADDING_NEW_PRODUCTS_DETAILS') }));
      let response = await createProduct(payload);
      dispatch(showSuccessSnackbar({ message: t('NEW_PRODUCT_ADDED') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const reactivateProduct = createAsyncThunk(
  'products/reactivateProduct',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('REACTIVATING_PRODUCT') }));
      let response: any = await reactiveProduct(payload);
      // return await handleResponse(
      //   response,
      //   fulfillWithValue,
      //   rejectWithValue,
      //   dispatch
      // );
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('PRODUCT_REACTIVATED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(
          showErrorSnackbar({ message: t('PRODUCT_REACTIVATION_FAILED') })
        );
        return rejectWithValue(response);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deactivateProduct = createAsyncThunk(
  'products/deactivateProduct',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DEACTIVATING_PRODUCT') }));
      let response: any = await deactiveProduct(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('PRODUCT_DEACTIVATED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(
          showErrorSnackbar({ message: t('PRODUCT_DEACTIVATION_FAILED') })
        );
        return rejectWithValue(response);
      }
      // return await handleResponse(response, fulfillWithValue, rejectWithValue, dispatch);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const unavailableProduct = createAsyncThunk(
  'products/unavailableProduct',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('UPDATING_PRODUCT') }));
      let response = await unAvailableProduct(payload);
      return await handleResponse(
        response,
        fulfillWithValue,
        rejectWithValue,
        dispatch
      );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadProducts = createAsyncThunk(
  'products/downloadProducts',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_PRODUCT') }));
      let response: any = await getProductsByPage(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadProductDetails = createAsyncThunk(
  'products/downloadProductDetails',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_PRODUCT_DETAILS') }));
      let response: any = await getProduct(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchProductNames = createAsyncThunk(
  'products/fetchProductNames',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_PRODUCT_NAMES') }));
      return fulfillWithValue(await getProductNames(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const addProductToStores = createAsyncThunk(
  'products/addProductToStores',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('MODIFYING_PRODUCTS_DETAILS') }));
      let response = await addProductToAllStores(payload);
      return await handleResponse(
        response,
        fulfillWithValue,
        rejectWithValue,
        dispatch
      );
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);
